import { Euler, Matrix4, Quaternion, Vector3 } from 'three';

const UP = new Vector3(0, 1, 0);

/**
 * How the traveller is carried.
 *
 * The body walks the helix: a position at the walking radius, an eye at
 * 1.72 m, and a gaze aimed a few steps further down the rake, so the stair
 * always falls away ahead. The pointer only turns the head, never the body.
 *
 * When an eye goes to a telescope the whole rig is borrowed by the
 * instrument — position, orientation and field of view — and handed back
 * when the traveller lets go.
 */
export class CameraRig {
  /**
   * @param {import('three').PerspectiveCamera} camera
   * @param {import('../geometry/spiral.js').Helix} helix
   * @param {object} settings
   */
  constructor(camera, helix, settings) {
    this.camera = camera;
    this.helix = helix;
    this.cfg = settings.camera;
    this.baseFov = settings.camera.fov;

    const s = settings.stair;
    this.walkRadius = s.innerRadius + this.cfg.walkRadiusBias * (s.outerRadius - s.innerRadius);

    this.basePosition = new Vector3();
    this.baseQuat = new Quaternion();
    this.headQuat = new Quaternion();

    this._m = new Matrix4();
    this._ahead = new Vector3();
    this._euler = new Euler(0, 0, 0, 'YXZ');
    this._q = new Quaternion();
    this._p = new Vector3();

    /** @type {{position: Vector3, quaternion: Quaternion}|null} */
    this.borrowed = null;
    this.engaged = 0; // 0 on the stair, 1 at the eyepiece
  }

  /** Hand the rig to a telescope's eyepiece object (or null to take it back). */
  borrow(object3d) {
    this.borrowed = object3d ?? null;
    if (object3d) this.lastBorrowed = object3d;
  }

  /**
   * @param {number} dt
   * @param {number} t journey position
   * @param {{x:number,y:number}} look pointer, in NDC
   * @param {number} velocity dt-normalised journey velocity
   * @param {number} elapsed
   */
  update(dt, t, look, velocity, elapsed) {
    const cfg = this.cfg;
    const helix = this.helix;

    // ── the body on the stair ────────────────────────────────────────────
    helix.pointAtRadius(t, this.walkRadius, this.basePosition);
    this.basePosition.y += cfg.eyeHeight;

    // A step is a small fall and a small recovery.
    const gait = Math.sin(elapsed * 2.1) * 0.012 + Math.sin(elapsed * 4.3) * 0.004;
    const rush = Math.min(1, Math.abs(velocity) * 7);
    this.basePosition.y += gait * (0.35 + rush);

    // The aim point: across the well, around the turn, a little below. See
    // `settings.camera.aimTurn` for why it is not the tangent.
    const aimAngle = helix.angleAt(t) + helix.handed * cfg.aimTurn;
    this._ahead.set(
      Math.cos(aimAngle) * cfg.aimRadius,
      this.basePosition.y - cfg.aimDrop,
      Math.sin(aimAngle) * cfg.aimRadius,
    );
    // Near the top there is nothing below yet, so lift the gaze into the
    // lantern; near the foot, lift it to the wall where the old men sit.
    if (t < 0.03) {
      // Nothing has fallen away yet: lift the gaze toward the lantern, but
      // only enough to see where the light is coming from — the stair still
      // has to be in the frame on the first breath.
      this._ahead.y += (1 - t / 0.03) * (cfg.aimDrop * 0.55);
    }
    if (t > 0.9) {
      // The last turn and a half: the eyes go down into the square and find
      // the wall where the old men are sitting. `arrival` is set by the
      // Elders layer; without it, the middle of the floor will do.
      const k = Math.min(1, (t - 0.9) / 0.075);
      const target =
        this.arrival ?? this._p.set(0, helix.topY - helix.height + 0.4, 0);
      this._ahead.lerp(target, k * k * (3 - 2 * k) * 0.95);
    }

    // Matrix4.lookAt uses the camera convention (+Z away from the target, so
    // the eye looks down -Z). Object3D.lookAt does the opposite and would
    // turn the traveller around to face the way they came.
    this._m.lookAt(this.basePosition, this._ahead, UP);
    this.baseQuat.setFromRotationMatrix(this._m);

    // A long stair leans you into the turn.
    const roll = -velocity * 0.9 * cfg.swayAmount * 12;
    this._euler.set(
      look.y * cfg.pitchRange,
      -look.x * cfg.yawRange,
      Math.max(-0.08, Math.min(0.08, roll)),
    );
    this.headQuat.setFromEuler(this._euler);
    this._q.copy(this.baseQuat).multiply(this.headQuat);

    // ── the instrument, if one has been lifted ───────────────────────────
    const target = this.borrowed ? 1 : 0;
    const rate = target > this.engaged ? 2.3 : 3.8;
    this.engaged += (target - this.engaged) * Math.min(1, dt * rate);
    if (this.engaged < 0.0004) {
      this.engaged = 0;
      this.lastBorrowed = null;
    }

    // While letting go we keep blending out of the instrument we just left,
    // so the return to the stair is a movement and not a cut.
    if (this.engaged > 0 && this.lastBorrowed) {
      if (!this._q2) this._q2 = new Quaternion();
      this.lastBorrowed.getWorldPosition(this._p);
      this.lastBorrowed.getWorldQuaternion(this._q2);
      const k = ease(this.engaged);
      this.basePosition.lerp(this._p, k);
      this._q.slerp(this._q2, k);
    }

    this.camera.position.copy(this.basePosition);
    this.camera.quaternion.copy(this._q);

    const fov = this.baseFov + (11.5 - this.baseFov) * ease(this.engaged);
    if (Math.abs(this.camera.fov - fov) > 0.01) {
      this.camera.fov = fov;
      this.camera.updateProjectionMatrix();
    }
    this.camera.updateMatrixWorld();

    return this.engaged;
  }
}

function ease(x) {
  return x * x * (3 - 2 * x);
}
